import {
    api_user_invited_post,
    api_user_login_post,
    api_user_patch,
    api_user_preference_set_post,
    api_user_verify_password_post,
    api_verification_post,
    Gene,
    UserGender,
    UserPreferences,
} from 'api'
import { setDB } from 'db'
import { cloneDefault, deepcopy, get_session_info, phoneRegex } from 'shared'
import { USER_WELCOMED } from 'store/dbLabels'
import { createStore, produce, unwrap } from 'solid-js/store'
import { addAlert, fetch_user, whatUserNeeds } from 'store/user'
import { set_expire } from './code'
import { USER_PREFERENCES_DEF } from 'shared/defs'

export const numDigits = 5

export enum LoginStages {
    PHONE_SECTION = 0,
    CODE_SECTION,
    PASS_SECTION,
    NAME_SECTION,
    HOMEPICK_SECTION,
    PREFERENCE_SECTION,
    INVITED_SECTION,
}

export type LOGIN_STATE_ID =
    'phone' | 'code' | 'pass' | 'name' | 'homepick' | 'preference' | 'invited'

export const LoginStageIds: Record<LOGIN_STATE_ID, LoginStages> = {
    phone: LoginStages.PHONE_SECTION,
    code: LoginStages.CODE_SECTION,
    pass: LoginStages.PASS_SECTION,
    name: LoginStages.NAME_SECTION,
    homepick: LoginStages.HOMEPICK_SECTION,
    preference: LoginStages.PREFERENCE_SECTION,
    invited: LoginStages.INVITED_SECTION,
} as const satisfies Record<string, LoginStages>

export const LoginEnumStages = Object.values(LoginStages).filter(
    (v): v is LoginStages => typeof v === 'number'
)

export type LOGIN_STATE = {
    stage: number

    isGoingCustomStages: boolean
    customStages: LOGIN_STATE_ID[]

    pref: UserPreferences

    showStages: boolean
    name: string
    gender: UserGender
    phone: string
    code: string
    gene: Gene
    secret: string
    loading: boolean
    error: string
    expires: number
    canResend: boolean
    pass: string
    passExpires: number
    passShow: boolean

    inviterGene: Gene

    next: string | null
}

const LOGIN_STATE_DEF: LOGIN_STATE = {
    stage: LoginStages.PHONE_SECTION,
    isGoingCustomStages: false,
    customStages: [],
    showStages: false,
    name: '',
    phone: '',
    code: '',
    loading: false,
    error: '',
    expires: -1,
    canResend: true,

    gender: 'male',

    pref: cloneDefault(USER_PREFERENCES_DEF),

    gene: '',
    secret: '',
    pass: '',
    passExpires: 0,
    passShow: false,

    inviterGene: null,

    next: null,
}

export const LOGIN_STAGES_FA = [
    {
        stage: LoginStages.NAME_SECTION,
        title: 'اسم شما',
    },
    {
        stage: LoginStages.HOMEPICK_SECTION,
        title: 'خانه',
    },
    {
        stage: LoginStages.PREFERENCE_SECTION,
        title: 'علاقه‌مندی هات',
    },
    {
        stage: LoginStages.INVITED_SECTION,
        title: 'کد دعوت',
    },
] as const

export const [lstate, setlState] = createStore<LOGIN_STATE>(
    cloneDefault(LOGIN_STATE_DEF)
)

export const setLoginStage = (stages: LOGIN_STATE_ID[]) => {
    const sortedStages = [...stages].sort(
        (a, b) => LoginStageIds[a] - LoginStageIds[b]
    )

    if (sortedStages.length === 0) return

    setlState({
        ...cloneDefault(LOGIN_STATE_DEF),
        customStages: sortedStages,
        isGoingCustomStages: true,
    })

    goToNextStep()
}

export const getStageClassList = (
    targetStage: LoginStages
): {
    loading: boolean
    right: boolean
    active: boolean
    left: boolean
} => ({
    loading: lstate.loading,
    right: lstate.stage < targetStage,
    active: lstate.stage === targetStage,
    left: lstate.stage > targetStage,
})

export const goToNextStep = () => {
    if (lstate.isGoingCustomStages) {
        if (lstate.customStages.length !== 0) {
            const [nextStageId, ...remainingStagesIds] = deepcopy(
                lstate.customStages
            )

            if (nextStageId === undefined) return reFetch()

            const nextStage = LoginStageIds[nextStageId]

            const showStages = LOGIN_STAGES_FA.some(s => s.stage === nextStage)

            if (nextStage === undefined) return reFetch()

            setlState({
                stage: nextStage,
                customStages: remainingStagesIds || [],
                showStages,
            })
        } else {
            reFetch()
        }
    } else {
        if (lstate.stage >= LoginEnumStages.length - 1) {
            reFetch()
        } else {
            setlState(
                produce(s => {
                    s.stage += 1
                })
            )
        }
    }
}

export async function verfication() {
    if (lstate.phone.length == 10 && lstate.phone[0] !== '0') {
        let old = deepcopy(lstate.phone)
        let newPhone = '0'.concat(old)

        setlState(produce(s => (s.phone = newPhone)))
    }

    if (lstate.phone.length !== 11)
        return setlState(
            produce(state => {
                state.error = 'شماره تلفن اشتباهه!'
            })
        )

    if (!phoneRegex.test(lstate.phone))
        return setlState(
            produce(state => {
                state.error = 'شماره تلفن اشتباهه!'
            })
        )

    setlState({
        loading: false,
    })
    goToNextStep()

    let res = await api_verification_post({
        action: 'login',
        telephone: {
            cc: 98,
            phone: lstate.phone,
        },
    })

    if (!res.ok()) {
        setlState({
            stage: LoginStages.PHONE_SECTION,
            expires: -1,
            error: 'خطا سرور، یه بار دیگه امتحان کن!',
        })

        return
    }

    const nextResend = res.body.next_resend || false

    if (!nextResend) {
        if (res.body.verify_expires > 0 && res.body.last_code_expires > 0) {
            setlState({
                expires: res.body.last_code_expires,
                canResend: false,
            })

            set_expire()

            return
        }

        setlState({
            stage: LoginStages.PHONE_SECTION,
            expires: -1,
            error: '',
        })
        addAlert({
            subject: 'لطفا چند دقیقه دیگه دوباره تلاش کن',
            timeout: 3,
            type: 'error',
        })

        return
    }

    setlState({
        expires: nextResend,
        canResend: true,
    })

    set_expire()

    return
}

export async function user_login() {
    let code = unwrap(lstate.code)

    if (code.length !== numDigits)
        return setlState({ loading: false, error: 'کد درست نیست!' })

    setlState({ loading: true })

    let res = await api_user_login_post(
        {
            code,
            info: get_session_info(),
            telephone: {
                phone: lstate.phone,
                cc: 98,
            },
        },
        {
            show_alert: false,
        }
    )

    if (res.ok()) {
        if (res.body.kind == 'logged_in') {
            if (res.body.created) {
                setlState({
                    showStages: true,
                    loading: false,
                    stage: LoginStages.NAME_SECTION,
                    name: '',
                })
            } else {
                reFetch()
            }
        } else {
            let r = res.body
            setlState(
                produce(s => {
                    s.loading = false
                    s.stage = LoginStages.PASS_SECTION
                    s.passExpires = r.expires
                    s.gene = r.gene!
                    s.secret = r.secret!
                })
            )
        }
    } else {
        setlState({ loading: false })
        if (res.status === 403) {
            setlState({
                error: 'محلت تایید کد تموم شد!',
                code: '',
            })
        } else {
            setlState({
                error: 'کد درست نیست!',
                code: '',
            })
        }
    }

    return
}

export const passExpired = () => {
    addAlert({
        timeout: 2,
        subject: 'وقت تایید تموم شد!',
        type: 'error',
    })

    setlState(
        produce(s => {
            s.stage = LoginStages.PHONE_SECTION
            s.code = ''
            s.loading = false
            s.error = ''
            s.expires = 0

            s.gene = ''
            s.secret = ''
            s.pass = ''
            s.passExpires = 0
        })
    )
}

export async function verify_pass() {
    if (lstate.passExpires <= 0) {
        passExpired()
        return
    }

    if (lstate.loading) return

    setlState({ loading: true })

    let res = await api_user_verify_password_post({
        gene: lstate.gene,
        password: lstate.pass,
        secret: lstate.secret,
    })

    setlState({ loading: false })

    if (!res.ok()) return

    const w = whatUserNeeds(res.body)

    if (w.length != 0) {
        setLoginStage(w)
        return
    }

    reFetch()
}

export async function set_name() {
    setlState({ loading: true })

    const name = unwrap(lstate.name)
    const gender = unwrap(lstate.gender)

    if (name.length == 0)
        return setlState({ loading: false, error: 'اسم خالی که نمیشه' })

    if (name.length <= 2)
        return setlState({ loading: false, error: 'اسم الکی نداریم' })

    let res = await api_user_patch(
        {
            gender,
            name,
        },
        { show_alert: true }
    )

    setlState({ loading: false })

    if (!res.ok()) return

    goToNextStep()
}

export async function set_preferences() {
    setlState({ loading: true })

    const preferences = deepcopy(lstate.pref)

    let res = await api_user_preference_set_post({
        ...preferences.eatery,
    })

    setlState({ loading: false })

    if (!res.ok()) return

    goToNextStep()
}

export async function check_invite() {
    const inviter = unwrap(lstate.inviterGene)
    if (inviter) {
        setlState({ loading: true })

        let res = await api_user_invited_post({ inviter })

        setlState({ loading: false })

        if (!res.ok()) return
    }

    goToNextStep()
}

const reFetch = () => {
    setDB(USER_WELCOMED, 'true')

    if (lstate.next) {
        window.open(lstate.next, '_self')
        setlState({
            next: null,
            loading: false,
        })
        return
    }

    const url = new URL(window.location.href)

    if (url.searchParams.has(globalThis.USER_INVITE_KEY)) {
        url.searchParams.delete(globalThis.USER_INVITE_KEY)
        window.open(url.toString(), '_self')
        return
    }

    fetch_user(true)
}
